import React from 'react';
import { isNullOrUndefined } from 'utils/object-utils';
import { ROUTE_CONSTANTS } from 'constants/util/route-constants';
import { ANNOTATION_DISPLAY_FUNCTIONS } from 'constants/feature/tieout-element-constants';
import FeatureFlagComponent from 'components/common/feature-flag-component';
import { FEATURE_NAMES } from 'constants/feature-flag-constants';
import { ANNOTATION_POSITION_TYPES } from '../constants/feature/annotation-elements-constants';
import { renderWorkpaperListOnStatementContent } from 'constants/feature/tieout-element-constants';

export const hasAnnotations = (elementDetails, isCftEnabled = true) => {
  return (
    !isNullOrUndefined(elementDetails) &&
    (elementDetails.hasInternalReferenceGroup() ||
      elementDetails.hasWorkpapers() ||
      elementDetails.hasTickmarks() ||
      (elementDetails.hasComfortLetters() && isCftEnabled) ||
      elementDetails.hasFormula())
  );
};

export const isElementReportPreviewPage = (url) =>
  url.includes(ROUTE_CONSTANTS.REPORT_PREVIEW);

export const getAnnotation = (elementDetails) => {
  const ELEMENT_BLOCK = 'tieout-element';
  return (
    <div className={`${ELEMENT_BLOCK}`}>
      <FeatureFlagComponent name={FEATURE_NAMES.COMFORT_LETTER_FEATURE}>
        {ANNOTATION_DISPLAY_FUNCTIONS.comfortLetter({
          elementDetails,
        })}
      </FeatureFlagComponent>
      {ANNOTATION_DISPLAY_FUNCTIONS.tickmarks({
        elementDetails,
      })}
      {ANNOTATION_DISPLAY_FUNCTIONS.formula({
        elementDetails,
      })}
      {ANNOTATION_DISPLAY_FUNCTIONS.internalRef({
        elementDetails,
      })}
      {ANNOTATION_DISPLAY_FUNCTIONS.workpaper({
        elementDetails,
      })}
    </div>
  );
};

export const getElementHighlightStatus = ({
  elementDetails,
  isElementSelected,
  isElementHighlighted,
  isPreviewPage = false,
  showElementStatusAndFlag = true,
  blacklineViewMode,
}) => {
  if (isNullOrUndefined(elementDetails)) {
    return {
      isReviewed: false,
      isVerified: false,
      isUnverified: false,
      isSelected: false,
    };
  }
  isElementSelected =
    typeof isElementSelected === 'function'
      ? isElementSelected()
      : isElementSelected;
  let isReviewed =
    elementDetails.isReviewed() && !isElementSelected && !isElementHighlighted;
  let isVerified =
    elementDetails.isVerified() && !isElementSelected && !isElementHighlighted;
  let isUnverified = !(
    elementDetails.isReviewed() ||
    elementDetails.isVerified() ||
    isElementSelected ||
    isElementHighlighted
  );

  if (isPreviewPage || blacklineViewMode) {
    isReviewed = showElementStatusAndFlag ? isReviewed : false;
    isVerified = showElementStatusAndFlag ? isVerified : false;
    isUnverified = showElementStatusAndFlag ? isUnverified : false;
    return {
      isReviewed,
      isVerified,
      isUnverified,
      isSelected: isElementSelected,
      isHighlighted: isElementHighlighted,
    };
  } else {
    return {
      isReviewed,
      isVerified,
      isUnverified,
      isSelected: isElementSelected,
      isHighlighted: isElementHighlighted,
    };
  }
};

export const getCssModifierForView = (
  url,
  blacklineViewMode,
  blacklineViewShowElementMode,
) => {
  if (blacklineViewMode && blacklineViewShowElementMode) {
    return 'blackline-view';
  }
  if (isElementReportPreviewPage(url)) {
    return 'report-preview';
  }
  return 'default';
};

export const getCalloutPosition = ({
  position,
  polygonInPixel,
  canvasHeight,
  canvasWidth,
  offsetHeight,
  offsetWidth,
  elementId,
  dynamicWidthBefore,
  isPreviewPage,
}) => {
  const width = polygonInPixel[2] - polygonInPixel[0];
  const MAX_POLYGON_WIDTH = 5;

  let callout = {};
  const [x1, y1, , , x3, y3] = polygonInPixel;
  const topLeftYCoordinate = y1;
  const topLeftXCoordinate = x1;
  const bottomLeftYCoordinate = y3;
  const bottomLeftXCoordinate = x3;
  const verticleMidPointOfElement = (y3 - y1) / 2;
  const horizontalMidPointOfElement = (x3 - x1) / 2;
  const arrowPaddingLeftRight = parseFloat(dynamicWidthBefore) - 1;
  const arrowPaddingTopBottom = 3;
  const spacePadding = isPreviewPage ? -1 : width > MAX_POLYGON_WIDTH ? 1 : 4;

  switch (position) {
    case 'left':
      callout = {
        bottom: `${
          canvasHeight -
          topLeftYCoordinate -
          (offsetHeight && Object.keys(offsetHeight).length > 0
            ? offsetHeight[elementId]
            : 0) /
            2 -
          verticleMidPointOfElement
        }px`,
        right: `${
          canvasWidth -
          topLeftXCoordinate +
          arrowPaddingLeftRight +
          spacePadding
        }px`,
      };
      break;
    case 'right':
      callout = {
        bottom: `${
          canvasHeight -
          topLeftYCoordinate -
          (offsetHeight && Object.keys(offsetHeight).length > 0
            ? offsetHeight[elementId]
            : 0) /
            2 -
          verticleMidPointOfElement
        }px`,
        right: `${
          canvasWidth -
          bottomLeftXCoordinate -
          arrowPaddingLeftRight -
          spacePadding
        }px`,
      };
      break;
    case 'top':
      callout = {
        bottom: `${
          canvasHeight -
          topLeftYCoordinate +
          arrowPaddingTopBottom +
          spacePadding +
          1
        }px`,
        right: `${
          canvasWidth -
          topLeftXCoordinate -
          (offsetWidth && Object.keys(offsetWidth).length > 0
            ? offsetWidth[elementId]
            : 0) /
            2 -
          horizontalMidPointOfElement
        }px`,
      };
      break;
    case 'bottom':
      callout = {
        bottom: `${
          canvasHeight -
          bottomLeftYCoordinate -
          offsetHeight[elementId] -
          arrowPaddingTopBottom -
          spacePadding
        }px`,
        right: `${
          canvasWidth -
          topLeftXCoordinate -
          (offsetWidth && Object.keys(offsetWidth).length > 0
            ? offsetWidth[elementId]
            : 0) /
            2 -
          horizontalMidPointOfElement
        }px`,
      };
      break;

    default:
      break;
  }
  return callout;
};
export const getShowCalloutValue = (
  elementData,
  annotationDisplayFilter,
  shouldShowAnnotationsByMode,
) => {
  if (isNullOrUndefined(elementData)) {
    return true;
  }

  // if left view of side by side, show all annotations irrespective of filter applied by user as per requirement only in
  // Auto tieout mode and trial balance mode, for other modes show or hide annotations as per filter applied by user
  if (shouldShowAnnotationsByMode) {
    return true;
  }

  const annotationsConfig = {
    tickmarks: elementData.hasTickmarks(),
    formula: elementData.hasFormula(),
    internalRef: elementData.hasInternalReferenceGroup(),
    workpaper: elementData.hasWorkpapers(),
    comfortLetter: elementData.hasComfortLetters(),
  };
  const enabled = {};
  let disabledCount = 0;

  for (const [key, value] of Object.entries(annotationsConfig || {})) {
    if (value) {
      enabled[key] = value;
    }
  }
  const enabledKeys = Object.keys(enabled);

  enabledKeys.forEach((annotation) => {
    if (
      annotation === 'workpaper' &&
      annotationDisplayFilter?.workpapersSelected?.length > 0
    ) {
      const workpaperList = renderWorkpaperListOnStatementContent(
        elementData,
        false,
        annotationDisplayFilter?.workpapersSelected,
        false, // explicitly: do not uppercase in OCR annotation context
      );

      // Check if all values in the array are null
      const allNull =
        Array.isArray(workpaperList) &&
        workpaperList.every((item) => item === null);

      if (allNull || !workpaperList) {
        disabledCount += 1;
      }
    } else if (!annotationDisplayFilter[annotation]) {
      disabledCount += 1;
    }
  });
  return !(disabledCount === enabledKeys.length);
};

export const getMarkerPosition = ({
  position,
  polygonInPixel,
  canvasHeight,
  markerWidth = 7,
  markerPadding = 3,
  markerHeight = 2,
  sPadding,
}) => {
  const width = polygonInPixel[2] - polygonInPixel[0];
  const MAX_POLYGON_WIDTH = 5;

  let marker = {};
  const [x1, y1, , , x3, y3] = polygonInPixel;

  const topLeftYCoordinate = y1;
  const topLeftXCoordinate = x1;
  const bottomLeftYCoordinate = y3;
  const bottomLeftXCoordinate = x3;
  const verticleMidPointOfElement = (y3 - y1) / 2;
  const horizontalMidPointOfElement = (x3 - x1) / 2;
  const spacePadding = sPadding || width > MAX_POLYGON_WIDTH ? 4 : 7;
  const rightSpacePadding = width > MAX_POLYGON_WIDTH ? 1.5 : 4;
  switch (position) {
    case 'left':
      marker = {
        bottom: `${
          canvasHeight -
          topLeftYCoordinate -
          verticleMidPointOfElement -
          markerPadding
        }px`,
        left: `${topLeftXCoordinate - markerWidth - spacePadding}px`,
      };
      break;
    case 'top':
      marker = {
        bottom: `${
          canvasHeight - topLeftYCoordinate - markerHeight + spacePadding
        }px`,
        left: `${
          topLeftXCoordinate +
          horizontalMidPointOfElement -
          markerWidth / 2 -
          markerHeight
        }px`,
      };
      break;
    case 'right':
      marker = {
        bottom: `${
          canvasHeight -
          topLeftYCoordinate -
          verticleMidPointOfElement -
          markerPadding
        }px`,
        left: `${bottomLeftXCoordinate - markerHeight + rightSpacePadding}px`,
      };
      break;
    case 'bottom':
      marker = {
        bottom: `${
          canvasHeight - bottomLeftYCoordinate - markerWidth - spacePadding
        }px`,
        left: `${
          topLeftXCoordinate +
          horizontalMidPointOfElement -
          markerWidth / 2 -
          markerHeight
        }px`,
      };
      break;

    default:
      break;
  }
  return marker;
};

export const getInitialCalloutVisibilityDetails = (
  config,
  annotationDisplayFilter,
) => {
  const displayMarker = annotationDisplayFilter.showAll ? false : config.marker;
  const displayCallout = annotationDisplayFilter.showAll
    ? true
    : config.callout;
  const displayAnnotation = annotationDisplayFilter.showAll
    ? true
    : config.display;

  return { displayMarker, displayCallout, displayAnnotation };
};

export const getCalloutVisibilityDetails = (
  calloutVisibilityDetails,
  annotationsConfig,
  annotationDisplayFilter,
  shouldShowAnnotationsByMode,
  elementDetails,
) => {
  const enabled = {};
  let disabledCount = 0;
  let displayMarker = calloutVisibilityDetails.displayMarker;
  let displayCallout = calloutVisibilityDetails.displayCallout;
  let displayAnnotation = calloutVisibilityDetails.displayAnnotation;

  // if left view of side by side, show all annotations irrespective of filter applied by user as per requirement only in
  // Auto tieout mode and trial balance mode, for other modes show or hide annotations as per filter applied by user
  if (shouldShowAnnotationsByMode) {
    return {
      displayMarker,
      displayCallout,
      displayAnnotation,
    };
  }

  for (const [key, value] of Object.entries(annotationsConfig || {})) {
    if (value) {
      enabled[key] = value;
    }
  }

  const enabledKeys = Object.keys(enabled);

  enabledKeys.forEach((annotation) => {
    if (
      annotation === 'workpaper' &&
      annotationDisplayFilter?.workpapersSelected?.length > 0
    ) {
      const workpaperList = renderWorkpaperListOnStatementContent(
        elementDetails,
        false,
        annotationDisplayFilter?.workpapersSelected,
        false, // explicitly: do not uppercase in OCR annotation context
      );

      // Check if all values in the array are null
      const allNull =
        Array.isArray(workpaperList) &&
        workpaperList.every((item) => item === null);

      if (allNull || !workpaperList) {
        disabledCount += 1;
      }
    } else if (!annotationDisplayFilter[annotation]) {
      disabledCount += 1;
    }
  });

  if (disabledCount === enabledKeys.length) {
    displayMarker = true;
    displayCallout = false;
    displayAnnotation = false;
  }

  return {
    displayMarker,
    displayCallout,
    displayAnnotation,
  };
};

export const getDraggedCalloutPosition = ({ draggedX, draggedY, polygon }) => {
  if (draggedX >= polygon[0] && draggedX <= polygon[2]) {
    if (draggedY <= polygon[1]) {
      return ANNOTATION_POSITION_TYPES.TOP; // Above the rectangle
    } else if (draggedY >= polygon[7]) {
      return ANNOTATION_POSITION_TYPES.BOTTOM; // Below the rectangle
    }
  } else if (draggedY >= polygon[1] && draggedY <= polygon[7]) {
    if (draggedX <= polygon[0]) {
      return ANNOTATION_POSITION_TYPES.LEFT; // Left of the rectangle
    } else if (draggedX >= polygon[2]) {
      return ANNOTATION_POSITION_TYPES.RIGHT; // Right of the rectangle
    }
  }
  return null; // No valid position found
};

export const getIntersection = (elementIds1, elementIds2) => {
  const [smallArr, largeArr] =
    elementIds1.length < elementIds2.length
      ? [elementIds1, elementIds2]
      : [elementIds2, elementIds1];
  const set = new Set(smallArr);

  return largeArr.filter((item) => set.has(item));
};
